/* ==========================================================================
   COMFY WORKS — boot + motion

   Progressive enhancement only. Every section is fully readable and
   shoppable if this file never executes. Attributes are namespaced data-cw-*
   so nothing here can collide with Horizon's own web components.
   ========================================================================== */

(() => {
  const root = document.documentElement;

  // Gate the reveal styles. Adding this before first paint of any observed
  // element avoids a flash of visible-then-hidden content.
  root.classList.add('js');

  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  // Module scope: the theme editor re-runs boot on shopify:section:load, and
  // capturing this inside the function would record whatever ground happened
  // to be applied at that moment instead of the page default.
  let groundBase = null;

  /* --- Scroll reveal ------------------------------------------------------
     One observer for the page. Targets unobserve once revealed — these are
     entrance animations, not scroll-linked state.                          */
  const initReveal = (scope = document) => {
    const targets = scope.querySelectorAll('[data-cw-reveal]:not(.is-revealed)');
    if (!targets.length) return;

    if (reducedMotion.matches || !('IntersectionObserver' in window)) {
      targets.forEach((el) => el.classList.add('is-revealed'));
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-revealed');
          observer.unobserve(entry.target);
        });
      },
      // Fire slightly before the element reaches the fold so the motion has
      // finished by the time it is properly in view.
      { rootMargin: '0px 0px -12% 0px', threshold: 0.01 }
    );

    targets.forEach((el) => {
      // Anything already on screen at boot reveals on its own timer instead of
      // waiting for an intersection.
      //
      // The -12% bottom margin above shrinks the observer's root, so content
      // sitting in the lowest 12% of the first screen never intersects and
      // never reveals — it just stays at opacity 0. The hero copy is pinned to
      // the bottom of a tall hero, which lands it exactly there: the headline,
      // note and CTA were all in the DOM and all invisible.
      const box = el.getBoundingClientRect();
      const onFirstScreen = box.top < window.innerHeight && box.bottom > 0;

      if (onFirstScreen) {
        const delay = parseFloat(getComputedStyle(el).transitionDelay) * 1000 || 0;
        // rAF so the opacity-0 start state paints first and the transition runs.
        requestAnimationFrame(() => {
          window.setTimeout(() => el.classList.add('is-revealed'), delay ? 0 : 0);
        });
        return;
      }

      observer.observe(el);
    });
  };

  /* --- Material ground ----------------------------------------------------
     Sections declaring data-cw-ground repaint the page as they take over the
     viewport, so scrolling between materials feels like moving between rooms
     rather than passing coloured bands. The -45% margins mean the swap
     happens when a section owns the middle of the screen.                  */
  const initMaterialGround = () => {
    const sections = document.querySelectorAll('[data-cw-ground]');
    if (!sections.length || !('IntersectionObserver' in window)) return;

    const body = document.body;
    // Captured once, before anything has claimed the ground.
    if (groundBase === null) groundBase = body.dataset.material || '';

    /* Track which ground-claiming sections currently own the middle of the
       screen. The previous version returned early on exit, so once the dark
       section had set the body to ink nothing ever put it back — scroll to
       the bottom, scroll up, and every cream section was black. */
    const active = new Set();

    const apply = () => {
      // Last one in wins; with -45% margins at most one qualifies anyway.
      const claimed = [...active].pop();
      const next = claimed ? claimed.dataset.cwGround : groundBase;
      if (next) body.dataset.material = next;
      else body.removeAttribute('data-material');
    };

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) active.add(entry.target);
          else active.delete(entry.target);
        });
        apply();
      },
      { rootMargin: '-45% 0px -45% 0px' }
    );

    sections.forEach((el) => observer.observe(el));
  };

  /* --- Header condense ----------------------------------------------------- */
  const initHeader = () => {
    const header = document.querySelector('[data-cw-header]');
    if (!header) return;

    let ticking = false;
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        header.classList.toggle('is-condensed', window.scrollY > 24);
        ticking = false;
      });
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
  };

  /* --- Watchdog -----------------------------------------------------------
     Last line of defence. If anything at all stops the observer revealing an
     element — a browser quirk, a layout that never intersects, an exception
     in another script — every remaining target is shown after this timeout.

     Content being briefly un-animated is a non-event. Content being
     permanently invisible is a broken shop, and that is what shipped before
     this existed.                                                          */
  const revealWatchdog = () => {
    window.setTimeout(() => {
      document
        .querySelectorAll('[data-cw-reveal]:not(.is-revealed)')
        .forEach((el) => {
          // Only force elements at or above the fold; anything further down
          // is legitimately waiting its turn.
          const box = el.getBoundingClientRect();
          if (box.top < window.innerHeight * 1.5) el.classList.add('is-revealed');
        });
    }, 2500);
  };

  const boot = () => {
    initReveal();
    initMaterialGround();
    initHeader();
    revealWatchdog();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }

  // Sections re-render independently in the theme editor. Re-scan rather than
  // leaving a freshly injected block stuck at opacity 0.
  document.addEventListener('shopify:section:load', (event) => {
    initReveal(event.target || document);
    initMaterialGround();
  });
})();
